import { encode, useIntl, type WalletCurrency, WithSkeleton } from '@capsicum/common';
import { Divider, EvolutionPercentage, ListCard, SkeletonItem } from '@capsicum/common-web';
import { type MarketAvailableWithPercentage, useWalletFormatter } from '@capsicum/socios-core';
import { memo } from 'react';
import styled from 'styled-components';

import { BuySellButtons } from './BuySellButtons';

export const MarketFanTokenCardSkeleton = () => {
	return (
		<ListCard>
			<ListCard.Start>
				<ListCard.Image isLoading src="" />
			</ListCard.Start>
			<ListCard.Content>
				<ListCard.Row>
					<SkeletonItem height={15} width={200} />
				</ListCard.Row>
				<Divider orientation="horizontal" />
				<ListCard.Row>
					<SkeletonItem height={15} width={170} />
				</ListCard.Row>
			</ListCard.Content>
			<ListCard.End></ListCard.End>
		</ListCard>
	);
};

export type MarketFanTokenCardComponentProps = {
	amount?: string;
	currency?: WalletCurrency;
	isBuyEnabled: boolean;
	isBuyLoading: boolean;
	isSellEnabled: boolean;
	isSellLoading: boolean;
	logo?: string;
	market?: MarketAvailableWithPercentage;
	name?: string;
	onClickBuy?: (fanTokenCurrencyId?: string) => void;
	onClickSell?: (fanTokenCurrencyId?: string) => void;
	onPartnerLogoClick?: (partnerId?: string) => void;
	partnerId?: string;
	showBuy?: boolean;
	showSell?: boolean;
	testID: string;
};

export const MarketFanTokenCardComponent = ({
	amount,
	currency,
	isBuyEnabled,
	isBuyLoading,
	isSellEnabled,
	isSellLoading,
	logo,
	market,
	name,
	onClickBuy,
	onClickSell,
	onPartnerLogoClick,
	partnerId,
	showBuy = true,
	showSell = true,
	testID
}: MarketFanTokenCardComponentProps) => {
	const { $t } = useIntl();

	const { formatEvolutionPercentage, formatPrice } = useWalletFormatter();

	const formattedAmount = formatPrice({ amount, currency });

	return (
		<S.ListCardWrapper testID={testID} onClick={() => onPartnerLogoClick?.(partnerId)}>
			<ListCard.Start>
				<ListCard.Image optimized src={logo} testID={testID} onClick={() => onPartnerLogoClick?.(partnerId)} />
			</ListCard.Start>
			<ListCard.Content>
				<ListCard.Row>
					<ListCard.Title>{name}</ListCard.Title>
				</ListCard.Row>
				<Divider orientation="horizontal" />
				<ListCard.Row>
					{market ? (
						<S.Subtitle>
							<S.BaseCurrency>{`1 ${market.baseCurrency?.shortCode}`}</S.BaseCurrency>
							<S.Amount>{`${encode('doubleTilde')}${formattedAmount}`}</S.Amount>
							<EvolutionPercentage
								$fontSize="text4"
								isPercentagePositive={market.isPercentagePositive}
								isPercentageZero={market.isPercentageZero}
								marketEvolutionPercentage={formatEvolutionPercentage(
									market.ticker?.evolutionPercentage
								)}
							/>
						</S.Subtitle>
					) : (
						<S.Subtitle>
							<S.NoAvailableMarketPerPartner>
								{$t('wallet.market.noMarketAvailablePerPartner')}
							</S.NoAvailableMarketPerPartner>
						</S.Subtitle>
					)}
				</ListCard.Row>
				<ListCard.Row>
					<S.BuySellButtonsContainer onClick={e => e.stopPropagation()}>
						<BuySellButtons
							fanTokenCurrencyId={market?.baseCurrency?.id}
							isBuyEnabled={isBuyEnabled}
							isBuyLoading={isBuyLoading}
							isMarketAvailable={!!market}
							isSellEnabled={isSellEnabled}
							isSellLoading={isSellLoading}
							partnerId={partnerId}
							showBuy={showBuy}
							showSell={showSell}
							testID={testID}
							onClickBuy={onClickBuy}
							onClickSell={onClickSell}
						/>
					</S.BuySellButtonsContainer>
				</ListCard.Row>
			</ListCard.Content>
			<ListCard.End></ListCard.End>
		</S.ListCardWrapper>
	);
};

export const MarketFanTokenCard = memo(
	WithSkeleton(MarketFanTokenCardComponent, MarketFanTokenCardSkeleton),
	(prevProps, nextProps) => {
		// This is to prevent re-rendering of the whole list when buy or sell is clicked and only re-render the clicked market
		return prevProps.isSellLoading === nextProps.isSellLoading && prevProps.isBuyLoading === nextProps.isBuyLoading;
	}
);

namespace S {
	export const ListCardWrapper = styled(ListCard)`
		height: auto;
	`;
	export const BaseCurrency = styled(ListCard.SubTitle)`
		${({ theme }) => theme.fonts.text.text5}
	`;
	export const NoAvailableMarketPerPartner = styled(ListCard.SubTitle)`
		${({ theme }) => theme.fonts.text.text5}
	`;
	export const Amount = styled(ListCard.SubTitle)`
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.main.enabled}
	`;
	export const Subtitle = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: baseline;
	`;
	export const BuySellButtonsContainer = styled.div``;
}
